import React from 'react';
import { API } from '../api';
import { Field, Section, Toggle } from './ui';
import PhotoPosterPicker from './PhotoPosterPicker';
import {
  nextOccurrence, repeatLabel, seriesEnd, ordinalWeekdayPhrase, WEEK_OPTIONS,
} from '../utils/eventRecurrence';

/*
 * Club events: the things on at the club that are not competitions.
 *
 * The admin's list is a working list, so it shows everything — drafts,
 * cancelled, and ones already past. Members only ever see the published,
 * uncancelled, still-to-come ones, and the row says which is which rather
 * than leaving an admin to wonder why nobody turned up.
 *
 * Cost and time are free text on purpose. "Gold coin", "$10 a head" and
 * "from 5:30" are all real answers a club gives, and none of them survive
 * being forced into a number or a time picker.
 */

const BLANK = {
  name: '', kind: '', startDate: '', startTime: '', endDate: '',
  location: '', cost: '', details: '', published: false,
  category: 'standard', repeat: '', repeatUntil: '', repeatSkips: [], repeatWeek: '',
  showOnScreen: true,
};

/* Three categories, because a club reads them differently. Special is the
   one it promotes; standard is the ordinary run of the year; internal is
   club business, and a member never sees it. */
const CATEGORIES = [
  ['standard', 'Standard club event'],
  ['special', 'Special event'],
  ['internal', 'Internal — committee only'],
];

const REPEATS = [
  ['', 'One-off'],
  ['weekly', 'Every week'],
  ['fortnightly', 'Every second week'],
  ['monthly', 'Every month, on the date'],
  ['monthly-nth', 'Every month, on the weekday'],
];

const fmtDate = iso => {
  if (!iso) return 'No date yet';
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  return Number.isNaN(d.getTime())
    ? 'No date yet'
    : d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

/* A series is over when the series is, not when its first night was: the
   Friday nights are still on even though the first one was in March. */
const isPast = ev => {
  const last = ev.repeat && ev.startDate ? seriesEnd(ev) : (ev.endDate || ev.startDate);
  return last ? String(last).slice(0, 10) < new Date().toISOString().slice(0, 10) : false;
};

function EventForm({ value, onChange, onSave, onCancel, saving, error }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="event-form">
      <div className="gs-field-grid" style={{ '--gs-cols': '2fr 1fr' }}>
        <Field label="What is it" htmlFor="ev-name" help="Printed as the heading on any poster.">
          <input id="ev-name" className="w3-input" placeholder="Club Quiz Night"
            value={value.name} onChange={e => set('name', e.target.value)} />
        </Field>
        <Field label="Kind" htmlFor="ev-kind" help="Quiz night, market day, working bee…">
          <input id="ev-kind" className="w3-input" placeholder="Quiz night"
            value={value.kind} onChange={e => set('kind', e.target.value)} />
        </Field>
      </div>

      <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr', marginTop: 'var(--gs-space-4)' }}>
        <Field label="Category" htmlFor="ev-category"
          help={value.category === 'internal'
            ? 'Committee only. It stays off the club screen and out of what members see, published or not.'
            : 'Special is the one the club promotes. Internal is club business only.'}>
          <select id="ev-category" className="w3-select w3-border"
            value={value.category || 'standard'} onChange={e => set('category', e.target.value)}>
            {CATEGORIES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </Field>
        <Field label="Repeats" htmlFor="ev-repeat"
          help="A Friday night is one event that repeats, not fifty events.">
          <select id="ev-repeat" className="w3-select w3-border"
            value={value.repeat || ''} onChange={e => set('repeat', e.target.value)}>
            {REPEATS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </Field>
      </div>

      <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr 1fr', marginTop: 'var(--gs-space-4)' }}>
        <Field label="Date" htmlFor="ev-date" help="Leave blank if it is not settled.">
          <input id="ev-date" type="date" className="w3-input"
            value={value.startDate || ''} onChange={e => set('startDate', e.target.value)} />
        </Field>
        <Field label="Time" htmlFor="ev-time" help="In your words — “7pm”, “from 5:30”.">
          <input id="ev-time" className="w3-input" placeholder="7pm"
            value={value.startTime} onChange={e => set('startTime', e.target.value)} />
        </Field>
        <Field label="Ends" htmlFor="ev-end" help="Only if it runs more than a day.">
          <input id="ev-end" type="date" className="w3-input"
            value={value.endDate || ''} onChange={e => set('endDate', e.target.value)} />
        </Field>
      </div>

      {/* Only what a series needs, and only once it is one. A "repeats
          until" box on a one-off market day is a question with no answer. */}
      {value.repeat && (
        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr', marginTop: 'var(--gs-space-4)' }}>
          {/* Which week of the month. The weekday itself comes from the date
              above — a club picking "the second Monday" has already told us
              Monday by choosing one, and asking twice invites the two
              answers to disagree. */}
          {value.repeat === 'monthly-nth' && (
            <Field label="Which week" htmlFor="ev-week"
              help={value.startDate
                ? ordinalWeekdayPhrase({ startDate: value.startDate, repeatWeek: value.repeatWeek })
                : 'Pick the date first — the weekday comes from it.'}>
              <select id="ev-week" className="w3-select w3-border"
                value={value.repeatWeek || ''} onChange={e => set('repeatWeek', e.target.value)}>
                <option value="">Same week as the date</option>
                {WEEK_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
              </select>
            </Field>
          )}
          <Field label="Repeats until" htmlFor="ev-until"
            help="Leave blank and it runs a year out.">
            <input id="ev-until" type="date" className="w3-input"
              value={value.repeatUntil || ''} onChange={e => set('repeatUntil', e.target.value)} />
          </Field>
          <Field label="Nights it skips" htmlFor="ev-skip"
            help="The Friday the clubhouse is booked. The rest of the series stays.">
            <input id="ev-skip" type="date" className="w3-input"
              onChange={e => {
                const d = e.target.value;
                if (!d) return;
                const skips = value.repeatSkips || [];
                if (!skips.includes(d)) set('repeatSkips', [...skips, d].sort());
                e.target.value = '';
              }} />
            {(value.repeatSkips || []).length > 0 && (
              <div className="event-skip-list">
                {(value.repeatSkips || []).map(d => (
                  <button type="button" key={d} className="w3-button w3-round poster-btn event-skip-chip"
                    title="Put this one back"
                    onClick={() => set('repeatSkips', (value.repeatSkips || []).filter(x => x !== d))}>
                    {fmtDate(d)} ×
                  </button>
                ))}
              </div>
            )}
          </Field>
        </div>
      )}

      <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr', marginTop: 'var(--gs-space-4)' }}>
        <Field label="Where" htmlFor="ev-where">
          <input id="ev-where" className="w3-input" placeholder="The clubhouse"
            value={value.location} onChange={e => set('location', e.target.value)} />
        </Field>
        <Field label="Cost" htmlFor="ev-cost" help="Words, not a number. “Free”, “Gold coin”, “$10 a head”.">
          <input id="ev-cost" className="w3-input" placeholder="$10 a head"
            value={value.cost} onChange={e => set('cost', e.target.value)} />
        </Field>
      </div>

      <div style={{ marginTop: 'var(--gs-space-4)' }}>
        <Field label="Details" htmlFor="ev-details"
          help="What people should know. This goes on the poster too.">
          <textarea id="ev-details" className="w3-input" style={{ minHeight: 72, resize: 'vertical' }}
            placeholder="Teams of six. Prizes, raffle and a supper. Bar open from 6."
            value={value.details} onChange={e => set('details', e.target.value)} />
        </Field>
      </div>

      {/* The club screen's noticeboard. On by default, and off is how a club
          keeps something out of the clubrooms without unpublishing it — an
          event with a poster is shown as the poster instead, so this only
          decides the ones without one. */}
      <div style={{ marginTop: 'var(--gs-space-4)' }}>
        <label className="poster-switch">
          <Toggle
            ariaLabel={`Show "${value.name || 'this event'}" on the club screen`}
            checked={value.showOnScreen !== false}
            disabled={value.category === 'internal'}
            onChange={() => set('showOnScreen', value.showOnScreen === false)} />
          <span>Show on the club screen</span>
        </label>
        <p className="poster-note" style={{ marginTop: 'var(--gs-space-2)' }}>
          {value.category === 'internal'
            ? 'Committee-only events never reach the club screen.'
            : 'On the noticeboard in the clubrooms. An event with a poster shows as its poster instead.'}
        </p>
      </div>

      {error && <div className="poster-error" role="alert">{error}</div>}

      <div className="poster-actions" style={{ marginTop: 'var(--gs-space-4)' }}>
        <button type="button" className="w3-button w3-round poster-btn-primary"
          disabled={saving || !value.name.trim()} onClick={onSave}>
          {saving ? 'Saving…' : 'Save event'}
        </button>
        <button type="button" className="w3-button w3-round poster-btn" onClick={onCancel}>Cancel</button>
        <span className="poster-note">
          Saving does not publish it. Members see it once you switch it on.
        </span>
      </div>
    </div>
  );
}

function EventsTab({ clubId, showToast }) {
  const [events, setEvents] = React.useState([]);
  const [draft, setDraft] = React.useState(null);
  const [editingId, setEditingId] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  // The event a gallery photo is being chosen for, as its poster.
  const [posterFor, setPosterFor] = React.useState(null);

  const load = React.useCallback(() => {
    if (!clubId) return;
    fetch(`${API}/api/events?clubId=${clubId}&all=1`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => setEvents(Array.isArray(d) ? d : []))
      .catch(() => setEvents([]));
  }, [clubId]);

  React.useEffect(load, [load]);

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      const body = { ...draft, clubId };
      const res = await fetch(`${API}/api/events${editingId ? `/${editingId}` : ''}`, {
        method: editingId ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Could not save that.'); return; }
      setDraft(null);
      setEditingId(null);
      load();
      showToast(editingId ? 'Event updated.' : 'Event added.');
    } catch (e) {
      setError(e.message || 'Could not save that.');
    } finally {
      setSaving(false);
    }
  };

  const patch = async (ev, body) => {
    const res = await fetch(`${API}/api/events/${ev.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (res.ok) { const u = await res.json(); setEvents(prev => prev.map(x => (x.id === u.id ? u : x))); }
  };

  const remove = async ev => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${ev.name}"? This cannot be undone.`)) return;
    const res = await fetch(`${API}/api/events/${ev.id}`, { method: 'DELETE' });
    if (res.ok) { setEvents(prev => prev.filter(x => x.id !== ev.id)); showToast('Event deleted.'); }
  };

  const startEdit = ev => {
    setEditingId(ev.id);
    setDraft({
      name: ev.name || '', kind: ev.kind || '',
      startDate: ev.startDate ? String(ev.startDate).slice(0, 10) : '',
      startTime: ev.startTime || '',
      endDate: ev.endDate ? String(ev.endDate).slice(0, 10) : '',
      location: ev.location || '', cost: ev.cost || '', details: ev.details || '',
      published: !!ev.published,
      category: ev.category || 'standard',
      repeat: ev.repeat || '',
      repeatUntil: ev.repeatUntil ? String(ev.repeatUntil).slice(0, 10) : '',
      repeatSkips: Array.isArray(ev.repeatSkips) ? ev.repeatSkips.map(d => String(d).slice(0, 10)) : [],
      repeatWeek: ev.repeatWeek || '',
      showOnScreen: ev.showOnScreen !== false,
    });
  };

  return (
    <div className="gs-panel">
      <Section
        label="Club events"
        action={!draft && (
          <button type="button" className="w3-button w3-round poster-btn-primary"
            onClick={() => { setEditingId(null); setDraft({ ...BLANK }); }}>
            Add an event
          </button>
        )}>
        <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          Anything on at the club that is not a competition — a market day, a quiz night, a working
          bee. Published events show on the club screen and can have a poster made for them.
        </p>

        {draft && (
          <EventForm value={draft} onChange={setDraft} onSave={save} saving={saving} error={error}
            onCancel={() => { setDraft(null); setEditingId(null); setError(''); }} />
        )}

        {events.length === 0 && !draft && (
          <p style={{ margin: 0, color: 'var(--gs-text-faint)' }}>No events yet.</p>
        )}

        {events.map(ev => {
          const past = isPast(ev);
          /* A series is dated by the night it is next on, not by the one it
             started on months ago. */
          const when = ev.repeat ? (nextOccurrence(ev) || ev.startDate) : ev.startDate;
          return (
            <div key={ev.id} className={`event-row${past || ev.cancelled ? ' event-row-dim' : ''}`}>
              <div className="event-when">
                <div className="event-date">{fmtDate(when)}</div>
                {ev.startTime && <div className="event-time">{ev.startTime}</div>}
              </div>

              <div className="event-main">
                <div className="event-name">
                  {ev.name}
                  {ev.category && ev.category !== 'standard' && (
                    <span className={`event-flag event-flag-${ev.category}`}>
                      {ev.category === 'internal' ? 'Committee only' : 'Special'}
                    </span>
                  )}
                  {ev.cancelled && <span className="event-flag event-flag-off">Cancelled</span>}
                  {!ev.published && !ev.cancelled && <span className="event-flag">Not published</span>}
                  {past && !ev.cancelled && <span className="event-flag">Been and gone</span>}
                </div>
                <div className="event-sub">
                  {[repeatLabel(ev), ev.kind, ev.location, ev.cost].filter(Boolean).join(' · ') || 'No details yet'}
                </div>
              </div>

              <div className="event-actions">
                <Toggle
                  ariaLabel={`Show "${ev.name}" to members`}
                  checked={!!ev.published}
                  disabled={!!ev.cancelled}
                  onChange={() => patch(ev, { published: !ev.published })} />
                <span className="event-pub">Members see it</span>
                <button type="button" className="w3-button w3-round poster-btn"
                  onClick={() => startEdit(ev)}>Edit</button>
                <button type="button" className="w3-button w3-round poster-btn"
                  title="Use a photo from the gallery as this event's poster"
                  onClick={() => setPosterFor(ev)}>
                  <i className="fa fa-image w3-margin-right"></i>Poster
                </button>
                <button type="button" className="w3-button w3-round poster-btn"
                  onClick={() => patch(ev, { cancelled: !ev.cancelled })}>
                  {ev.cancelled ? 'Reinstate' : 'Cancel'}
                </button>
                <button type="button" className="w3-button w3-round poster-btn poster-btn-danger"
                  onClick={() => remove(ev)}>Delete</button>
              </div>
            </div>
          );
        })}
      </Section>

      {posterFor && (
        <PhotoPosterPicker clubId={clubId} eventId={posterFor.id} subjectName={posterFor.name}
          onClose={() => setPosterFor(null)}
          onDone={() => showToast(`Poster set for ${posterFor.name}.`)} />
      )}
    </div>
  );
}

export default EventsTab;
export { EventForm, BLANK as BLANK_EVENT };
